<template>
  <v-card>
    <v-card-title class="grey lighten-3">
      <v-row>
        <v-icon class="draggable">mdi-drag</v-icon>
        <v-col class="py-0">{{ extra.name }} - Edit</v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pt-0">
      <v-container fluid pb-0>
        <v-layout row wrap>
          <v-flex xs12 sm3>
            <v-text-field
              label="Name"
              v-model="extraItem.name"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 pl-3>
            <dates-range-picker
              custom-date-from-label="Valid From"
              custom-date-to-label="Valid To"
              :gtDateFromString="extraItem.validFrom"
              :gtDateToString="extraItem.validTo"
              @onRangeSelected="onDatesRangeSelected($event)"
              :outlined="outlinedInputs"
            ></dates-range-picker>
          </v-flex>
          <v-flex xs12 sm3 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <agent-group-select
              :key="extraItem.id"
              :groups-only="true"
              :group-id="extraItem.groupId"
              @onSelect="extraItem.groupId = $event"
              :outlined="outlinedInputs"
            ></agent-group-select>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 sm4>
            <currency-list-select
              v-model="extraItem.currencyCode"
              :outlined="outlinedInputs"
            ></currency-list-select>
          </v-flex>
          <v-flex xs12 sm4 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <taxes-list-select
              v-model="extraItem.taxCode"
              :outlined="outlinedInputs"
            ></taxes-list-select>
          </v-flex>
          <v-flex xs12 sm4 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <rate-type-list-select
              :rate-type-code="extraItem.rateTypeCode"
              @onSelect="extraItem.rateTypeCode = $event.code"
              :outlined="outlinedInputs"
              hideNotInUse
            ></rate-type-list-select>
          </v-flex>
          <v-flex xs12 sm3 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <v-text-field
              label="% of price"
              v-model.number="extraItem.percentPrice"
              type="number"
              min="0"
              :outlined="outlinedInputs"
              :disabled="
                (extraItem.cost.toString() != '' &&
                  extraItem.cost.toString() != '0') ||
                (extraItem.sell.toString() != '' &&
                  extraItem.sell.toString() != '0')
              "
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm3 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <v-text-field
              label="Cost"
              v-model.number="extraItem.cost"
              type="number"
              min="0"
              :outlined="outlinedInputs"
              :disabled="
                extraItem.percentPrice.toString() != '' &&
                extraItem.percentPrice.toString() != '0'
              "
              @change="updateSellPrice"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm3 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <v-text-field
              label="Markup %"
              v-model.number="markup"
              type="number"
              min="0"
              :outlined="outlinedInputs"
              :disabled="
                extraItem.percentPrice.toString() != '' &&
                extraItem.percentPrice.toString() != '0'
              "
              @change="updateSellPrice"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm3 :class="{ 'pl-3': $vuetify.breakpoint.smAndUp }">
            <v-text-field
              label="Sell"
              v-model.number="extraItem.sell"
              type="number"
              min="0"
              :outlined="outlinedInputs"
              :disabled="
                extraItem.percentPrice.toString() != '' &&
                extraItem.percentPrice.toString() != '0'
              "
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.isChildOnly"
              label="Child Only"
              primary
              hide-details
              @change="onChildOnlyChanged"
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.isInfantOnly"
              label="Infant Only"
              primary
              hide-details
              @change="onInfantOnlyChanged"
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.isMandatory"
              label="Is Mandatory"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.noReport"
              label="No Report"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.commission"
              label="Commission"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.markup"
              label="Markup"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.discount"
              label="Discount"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.capacityChange"
              label="Capacity Change"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-if="$store.getters.hasAccessTo('third_party:marketplace')"
              v-model="extraItem.marketplace"
              label="Marketplace"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 sm2>
            <v-checkbox
              v-model="extraItem.noVoucher"
              label="No Voucher"
              primary
              hide-details
            ></v-checkbox>
          </v-flex>
        </v-layout>
      </v-container>
      <v-row v-if="debug">
        <code>{{ extraItem }}</code>
      </v-row>
    </v-card-text>
    <v-card-actions class="grey lighten-4">
      <v-layout>
        <v-flex class="text-right mr-4">
          <v-btn color="warning" text @click="onClose()">Cancel</v-btn>
          <v-btn color="primary" @click="onSave()" :disabled="isInputValid()"
            >Save</v-btn
          >
        </v-flex>
      </v-layout>
    </v-card-actions>
  </v-card>
</template>

<script>
import { EXTRA_UPDATE_MUTATION } from "../../../../constants/graphql";
import DatesRangePicker from "../../shared/DatesRangePicker";
import AgentGroupSelect from "../../shared/AgentGroupSelect";
import CurrencyListSelect from "../../shared/CurrencyListSelect";
import RateTypeListSelect from "../../shared/RateTypeListSelect";
import TaxesListSelect from "../../shared/TaxesListSelect.vue";

export default {
  name: "SupplierExtraEditor",

  components: {
    AgentGroupSelect,
    DatesRangePicker,
    CurrencyListSelect,
    RateTypeListSelect,
    TaxesListSelect,
  },

  props: {
    extra: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      debug: false,
      extraItem: this.extra ? { ...this.extra } : {},
      markup: 0,
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },

  methods: {
    updateSellPrice() {
      if (this.extraItem.cost && this.extraItem.cost > 0) {
        this.extraItem.sell =
          this.extraItem.cost + this.extraItem.cost * (this.markup / 100);
      }
    },
    onInfantOnlyChanged() {
      if (this.extraItem.isInfantOnly) {
        this.extraItem.isChildOnly = false;
      }
    },
    onChildOnlyChanged() {
      if (this.extraItem.isChildOnly) {
        this.extraItem.isInfantOnly = false;
      }
    },
    onDatesRangeSelected(data) {
      this.extraItem.validFrom = data.dateFrom;
      this.extraItem.validTo = data.dateTo;
    },
    onClose() {
      this.$emit("onClose");
    },
    onSave() {
      this.$apollo
        .mutate({
          mutation: EXTRA_UPDATE_MUTATION,
          variables: {
            id: this.extraItem.id,
            extraInput: {
              groupId: this.extraItem.groupId,
              name: this.extraItem.name,
              cost: this.extraItem.cost === "" ? 0 : this.extraItem.cost,
              sell: this.extraItem.sell === "" ? 0 : this.extraItem.sell,
              percentPrice:
                this.extraItem.percentPrice === ""
                  ? 0
                  : this.extraItem.percentPrice,
              currencyCode: this.extraItem.currencyCode,
              rateTypeCode: this.extraItem.rateTypeCode,
              validFrom: this.extraItem.validFrom,
              validTo: this.extraItem.validTo,
              isChildOnly: Boolean(this.extraItem.isChildOnly),
              isInfantOnly: Boolean(this.extraItem.isInfantOnly),
              capacityChange: Boolean(this.extraItem.capacityChange),
              markup: Boolean(this.extraItem.markup),
              commission: Boolean(this.extraItem.commission),
              discount: Boolean(this.extraItem.discount),
              isMandatory: Boolean(this.extraItem.isMandatory),
              noReport: Boolean(this.extraItem.noReport),
              marketplace: Boolean(this.extraItem.marketplace),
              noVoucher: Boolean(this.extraItem.noVoucher),
              taxCode: this.extraItem.taxCode,
            },
          },
        })
        .then((data) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Extra updated",
          });
          this.$emit("onSave");
        });
    },
    isInputValid() {
      return this.extraItem.name ? this.extraItem.name.trim() === "" : true;
    },
  },

  watch: {
    extra(data) {
      this.extraItem = { ...data, name: data.name.trim() };
      this.markup = Number(
        ((Number(this.extraItem.sell) - Number(this.extraItem.cost)) /
          Number(this.extraItem.cost)) *
          100
      ).toFixed(2);
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/extras/SupplierExtraEditor.vue